<script setup lang="ts">
    import { CardMenu } from '@/components/ui/card';
    import { columns } from '@/tables/columns/companies';
    import { Head } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import { useUrlSearch } from '@/composables/useUrlSearch';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateCompany from '@/components/companies/CreateCompany.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { BreadcrumbItem, NavItem } from '@/types';

    const { hasPermission } = usePermissions();

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.settings',
        },
        {
            title: 'companies.header',
            href: route('v2.companies.index'),
        },
    ];

    const navItems: NavItem[] = [
        {
            title: 'companies.header',
            href: route('v2.companies.index'),
            permissionLevel: Permission.COMPANIES_VIEW,
        },
        {
            title: 'company-affiliation.navbar_header',
            href: route('v2.company-groups.index'),
            permissionLevel: Permission.COMPANIES_VIEW,
        },
    ];

    const { isPending, data } = useQuery({
        queryKey: [QueryKey.COMPANIES],
        queryFn: getCompanies,
    });

    const { extractAndCleanSearch } = useUrlSearch();
    const initialSearch = extractAndCleanSearch();

    async function getCompanies() {
        const { data } = await axios.get(route('v2.companies.table'), {
            params: {
                search: initialSearch || undefined,
            },
        });
        return data.data;
    }
</script>

<template>
    <Head :title="$t('profile.profile_settings')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard v-if="hasPermission(Permission.COMPANIES_VIEW)">
            <DataTable
                :table-key="QueryKey.COMPANIES"
                :columns="columns"
                :data="data"
                :loading="isPending"
                :auto-url-search="true"
            >
                <template #title>
                    <CardMenu :items="navItems" />
                </template>
                <template #actions>
                    <DataTableToolbarActions name="companies">
                        <CreateCompany v-if="hasPermission(Permission.COMPANIES_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
